'use client';

import { useRef, type ForwardedRef, type ReactNode } from 'react';
import { mergeProps, useId, useObjectRef } from 'react-aria';
import { forwardRef } from '../../forward-ref.js';
import { IconSortAscending, IconSortDescending, IconSortUnsorted } from '../../icons/index.js';
import { useServerLayoutEffect } from '../../utils/use-server-layout-effect.js';
import type { CommonProps } from '../types.js';
import { type ColumnContentAlignment, useColumnRegistration } from './contexts/column-collection.js';
import { useTableRowState } from './contexts/row-state.js';
import { useTableState } from './contexts/table-state.js';
import { useTableCell } from './hooks/use-table-cell.js';
import { TableHeaderButton } from './table-header-button.js';

/** Represents the sort order of a table column. */
export type TableColumnSortOrder = 'ascending' | 'descending';

export interface TableHeaderCellProps extends CommonProps {
  /** The contents of the header cell. */
  children: ReactNode;
  /**
   * A unique identifier for the column that this cell belongs to.
   * Cells that belong to the same column must have the same `columnKey`.
   */
  columnKey: string;
  /**
   * The position of the column that this cell belongs to within the full dataset.
   *
   * Provide this value if your table presents a subset of columns, such as when the user toggles column visibility.
   *
   * This number must be:
   * - greater than or equal to `1`
   * - greater than the `columnNumber` of any previous cells in the current row
   * - less than or equal to the `totalColumnCount` of the parent `Table`
   *
   * Use together with `totalColumnCount` on the `Table` component.
   */
  columnNumber?: number;
  /** The width of the column. Can be any valid value of the `width` CSS property. */
  columnWidth?: string | number;
  /**
   * Horizontal alignment of cell content within the column.
   * @default 'start'
   */
  columnContentAlignment?: ColumnContentAlignment;
  /**
   * The current sort order of the column.
   */
  sortOrder?: TableColumnSortOrder;
  /**
   * A function called when the column's sort order should change.
   * If not provided, the column will not be sortable.
   */
  onSortOrderToggle?: () => void;
  /** An icon displayed before the header text. */
  iconStart?: ReactNode;
  /** An icon displayed after the header text. */
  iconEnd?: ReactNode;
}

const SORT_ORDER_ICONS: Record<TableColumnSortOrder, ReactNode> = {
  ascending: <IconSortAscending />,
  descending: <IconSortDescending />,
};

function TableHeaderCell(
  {
    children,
    UNSAFE_className,
    UNSAFE_style,
    columnKey,
    columnNumber,
    columnWidth,
    columnContentAlignment = 'start',
    sortOrder,
    onSortOrderToggle,
    iconStart,
    iconEnd,
    ...props
  }: TableHeaderCellProps,
  ref: ForwardedRef<HTMLTableCellElement>,
) {
  const cellRef = useObjectRef(ref);
  const { rowKey } = useTableRowState();
  const { cellProps } = useTableCell(cellRef, rowKey, columnKey);
  const registerColumn = useColumnRegistration();
  const unregisterRef = useRef<(() => void) | null>(null);

  const { sortLabelId } = useTableState();
  const contentId = useId();

  const isSortable = typeof onSortOrderToggle === 'function';

  // Columns need to be registered during SSR to prevent layout shifting on hydration
  useServerLayoutEffect(() => {
    unregisterRef.current = registerColumn(columnKey, {
      number: columnNumber,
      width: columnWidth,
      contentAlignment: columnContentAlignment,
    });

    return () => {
      unregisterRef.current?.();
      unregisterRef.current = null;
    };
  }, [registerColumn, columnKey, columnNumber, columnWidth, columnContentAlignment]);

  return (
    <th
      {...mergeProps(props, cellProps)}
      ref={cellRef}
      className={UNSAFE_className}
      style={columnWidth ? { ...UNSAFE_style, width: columnWidth } : UNSAFE_style}
      scope="col"
      aria-colindex={columnNumber}
      aria-sort={sortOrder}
      data-alignment={columnContentAlignment}
    >
      <div className="cim-table-header-cell-container">
        {iconStart}
        <span id={contentId}>{children}</span>
        {iconEnd}
        {isSortable ? (
          <TableHeaderButton
            UNSAFE_className="cim-table-header-sort-button"
            aria-labelledby={`${sortLabelId} ${contentId}`}
            icon={sortOrder ? SORT_ORDER_ICONS[sortOrder] : <IconSortUnsorted />}
            onPress={() => onSortOrderToggle()}
            data-highlighted={!!sortOrder || undefined}
          />
        ) : sortOrder ? (
          SORT_ORDER_ICONS[sortOrder]
        ) : undefined}
      </div>
    </th>
  );
}

/**
 * Renders a header cell within a table header row.
 *
 * See [table usage guidelines](https://ui.cimpress.io/components/table/) and [table building guide](https://ui.cimpress.io/dev-guides/tables/).
 */
const _TableHeaderCell = forwardRef(TableHeaderCell, 'TableHeaderCell');

export { _TableHeaderCell as TableHeaderCell };
